import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from '@mui/material';
import { jxScrollbarThin } from '../../../JxControls/jxScrollbarStyle';
import { tooltipSx } from '../jernyTooltipStyling';
import './JInspectorSelectEdit.css';

// Rough dropdown height budget — used only to decide open direction, not as exact size.
const ESTIMATED_DROPDOWN_HEIGHT = 220;

// Suggestion tooltip needs to render above the dropdown itself (zIndex 10001) — merged into
// one componentsProps object, same reasoning as JInspectorSelect's optionTooltipProps.
const suggestionTooltipProps = {
    tooltip: tooltipSx.tooltip,
    popper: { style: { ...tooltipSx.popper.style, zIndex: 10002 } },
};

/**
 * Single suggestion row — only shows its tooltip when the label is actually truncated.
 */
function JInspectorEditSuggestion({ label, className, onMouseEnter, onMouseDown }) {
    const [truncated, setTruncated] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const el = ref.current;
        if (el) setTruncated(el.scrollWidth > el.clientWidth);
    }, [label]);

    return (
        <Tooltip title={label} placement="bottom" componentsProps={suggestionTooltipProps} disableHoverListener={!truncated}>
            <div ref={ref} className={className} onMouseEnter={onMouseEnter} onMouseDown={onMouseDown}>
                {label}
            </div>
        </Tooltip>
    );
}

/**
 * JInspectorSelectEdit — free-text field with a filtered suggestion dropdown, styled to match
 * .j-inspector-select. Unlike JInspectorSelect (fixed option list only), typed text IS the
 * value — onChange fires on every keystroke, same "inline mode" as CsCodeGenAutocompleteEdit.
 * Options are just suggestions to speed up typing/reuse existing values, not a closed set.
 * @param {string} [id]
 * @param {string} value
 * @param {(value: string) => void} onChange
 * @param {Array<{label:string,value:string}>} options
 * @param {string} [placeholder]
 */
export default function JInspectorSelectEdit({ id, value, onChange, options, placeholder }) {
    const [open, setOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(0);
    const [dropdownStyle, setDropdownStyle] = useState({});
    const inputRef = useRef(null);
    const rootRef = useRef(null);

    const query = value ?? '';
    const filtered = query.trim() ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase())) : options;

    // Reset highlight when the filtered list changes
    useEffect(() => {
        setHighlighted(0);
    }, [query]);

    function positionDropdown() {
        const rect = rootRef.current?.getBoundingClientRect();
        if (!rect) return;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const openUpward = spaceBelow < ESTIMATED_DROPDOWN_HEIGHT && spaceAbove > spaceBelow;

        setDropdownStyle(
            openUpward
                ? {
                      position: 'fixed',
                      bottom: window.innerHeight - rect.top + 4,
                      left: rect.left,
                      width: rect.width,
                      maxHeight: spaceAbove - 8,
                      zIndex: 10001,
                  }
                : {
                      position: 'fixed',
                      top: rect.bottom + 4,
                      left: rect.left,
                      width: rect.width,
                      maxHeight: spaceBelow - 8,
                      zIndex: 10001,
                  },
        );
    }

    function select(option) {
        onChange(option.value);
        setOpen(false);
    }

    function handleFocus() {
        positionDropdown();
        setOpen(true);
    }

    // 120ms delay — mirrors CsCodeGenAutocompleteEdit: onMouseDown on a suggestion fires before
    // blur, so preventDefault there keeps focus; the delay covers "clicked away without
    // selecting" so the dropdown doesn't vanish out from under a pending click.
    function handleBlur() {
        setTimeout(() => setOpen(false), 120);
    }

    function handleChange(e) {
        onChange(e.target.value);
        positionDropdown();
        setOpen(true);
    }

    function handleKeyDown(e) {
        if (!open || filtered.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlighted((h) => Math.min(h + 1, filtered.length - 1));
            return;
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlighted((h) => Math.max(h - 1, 0));
            return;
        }
        if (e.key === 'Tab' || (e.key === 'Enter' && filtered[highlighted])) {
            // Only hijack Enter/Tab when a suggestion is actually highlighted — otherwise let
            // the typed free text stand as-is (it's already committed via onChange).
            e.preventDefault();
            select(filtered[highlighted]);
            return;
        }
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation(); // prevent JxForm global Escape-to-close from firing
            setOpen(false);
        }
    }

    const dropdown = open &&
        filtered.length > 0 &&
        createPortal(
            <div className="j-inspector-edit-dropdown" style={dropdownStyle}>
                <div className={`j-inspector-edit-listbox ${jxScrollbarThin}`}>
                    {filtered.map((o, i) => (
                        <JInspectorEditSuggestion
                            key={o.value}
                            label={o.label}
                            className={[
                                'j-inspector-edit-option',
                                o.value === value ? 'j-inspector-edit-option--selected' : '',
                                i === highlighted ? 'j-inspector-edit-option--active' : '',
                            ]
                                .filter(Boolean)
                                .join(' ')}
                            onMouseEnter={() => setHighlighted(i)}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                select(o);
                            }}
                        />
                    ))}
                </div>
            </div>,
            document.body,
        );

    return (
        <div ref={rootRef} className="j-inspector-edit-wrap">
            <input
                id={id}
                ref={inputRef}
                className="j-inspector-select"
                type="text"
                value={value ?? ''}
                placeholder={placeholder}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
            />
            {dropdown}
        </div>
    );
}
